import { useEffect, useRef, useState } from 'react';
import { useWidgetStore, useShadow } from '../state/createWidgetStore';
import { t } from '../i18n';
import { minimizeIcon, expandIcon, collapseIcon } from './icons';
import { MessageList } from './messageList/MessageList.tsx';
import { InputBar } from './inputBar/InputBar.tsx';
import { FileUploadOverlay } from './FileUpload.tsx';
import { updateAutoScrollState } from '../utils/scroll';
import { InactiveScreen } from './InactiveScreen.tsx';
export interface ChatPanelProps {
  isFullscreen: boolean;
  isFirstOpen: boolean;
  isPortal: boolean;
  onDrop: (file: File) => void;
}

export function ChatPanel({ isFullscreen, isFirstOpen, isPortal, onDrop }: ChatPanelProps) {
  const title = useWidgetStore((s) => s.config.title || t('title'));
  const authMode = useWidgetStore((s) => s.config.auth);
  const isExpanded = useWidgetStore((s) => s.isExpanded);
  const toggleExpanded = useWidgetStore((s) => s.toggleExpanded);
  const setState = useWidgetStore((s) => s.setState);
  const toggleOpen = useWidgetStore((s) => s.toggleOpen);
  const auth = useWidgetStore((s) => s.auth);
  const uploadEnabled = useWidgetStore((s) => s.config.uploadEnabled);
  const widgetInactive = useWidgetStore((s) => s.widgetInactive);

  const shadow = useShadow();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dragCounterRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  const verifiedEmail = authMode !== 'none' && auth.status === 'authenticated' && auth.email;
  const isAuthenticated = auth.status === 'authenticated';

  // Escape key collapses when expanded
  useEffect(() => {
    if (!isExpanded || isFullscreen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setState({ isExpanded: false });
        (shadow.host as HTMLElement).dispatchEvent(
          new CustomEvent('mcp-chat:collapse', { bubbles: true, composed: true }),
        );
      }
    };
    shadow.host.addEventListener('keydown', handler as EventListener);
    return () => shadow.host.removeEventListener('keydown', handler as EventListener);
  }, [isExpanded, isFullscreen, shadow, setState]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      updateAutoScrollState(scrollContainer);
    };

    handleScroll();
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []);

  // ─── drag and drop ─────────────────────────────────────────────────────────

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current++;
    if (dragCounterRef.current === 1) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDragging(false);

    if (!isAuthenticated || !uploadEnabled) return;

    const file = e.dataTransfer.files[0];
    if (file) onDrop(file);
  };

  // ─── header actions ─────────────────────────────────────────────────────────

  const handleMinimize = () => {
    toggleOpen();
    (shadow.host as HTMLElement).dispatchEvent(
      new CustomEvent('mcp-chat:close', { bubbles: true, composed: true }),
    );
  };

  const handleExpand = () => {
    const willExpand = !isExpanded;
    toggleExpanded();
    (shadow.host as HTMLElement).dispatchEvent(
      new CustomEvent(willExpand ? 'mcp-chat:expand' : 'mcp-chat:collapse', {
        bubbles: true,
        composed: true,
      }),
    );
  };

  const handleBackdropClick = () => {
    setState({ isExpanded: false });
    (shadow.host as HTMLElement).dispatchEvent(
      new CustomEvent('mcp-chat:collapse', { bubbles: true, composed: true }),
    );
  };

  // ─── render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {isExpanded && !isFullscreen && (
        <div className="panel-backdrop" onClick={handleBackdropClick} />
      )}
      <div
        className={`panel${isExpanded && !isFullscreen ? ' panel-expanded' : ''}${isFirstOpen ? ' panel-first-open' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Header */}
        <div className="panel-header">
          <h3>{title}</h3>
          {!isFullscreen && (
            <div className="header-actions">
              <button
                className="expand-btn"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
                onClick={handleExpand}
                dangerouslySetInnerHTML={{ __html: isExpanded ? collapseIcon : expandIcon }}
              />
              <button
                className="minimize-btn"
                aria-label={t('minimize')}
                onClick={handleMinimize}
                dangerouslySetInnerHTML={{ __html: minimizeIcon }}
              />
            </div>
          )}
        </div>

        {/* Verified email badge */}
        {verifiedEmail && (
          <div className="verified-badge">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="m9 12 2 2 4-4" />
            </svg>
            <span>{t('authVerified').replace('{email}', auth.email!)}</span>
          </div>
        )}

        {/* Body */}
        {widgetInactive ? (
          <InactiveScreen />
        ) : (
          <>
            <div className="panel-body" ref={scrollContainerRef}>
              <MessageList scrollContainerRef={scrollContainerRef} portalMode={isPortal} />
            </div>
            <div className="panel-footer">
              <InputBar shouldFocus={isFirstOpen} portalMode={isPortal} />
            </div>
            {isAuthenticated && uploadEnabled && (
              <FileUploadOverlay isDragging={isDragging} />
            )}
          </>
        )}
      </div>
    </>
  );
}
